import { isRef, markRaw, type Component, type Ref } from 'vue'

type AnyListener = (...args: unknown[]) => void
type RuntimeProps = Record<string, unknown>
type RuntimeListeners = Record<string, AnyListener>
type RuntimeModels = Record<string, Ref<unknown>>

export type ModalRenderEntry = {
  id: number
  component: Component
  props: RuntimeProps
  listeners: RuntimeListeners
  models: RuntimeModels
}

type RuntimeModalEntry = ModalRenderEntry & {
  resolve: (value: unknown) => void
  reject: () => void
}

type ComponentProps<TComponent extends Component> = TComponent extends new (
  ...args: any[]
) => { $props: infer TProps }
  ? TProps
  : never

type ListenerPropKey<TProps> = Extract<
  {
    [TKey in keyof TProps]: TKey extends `on${string}`
      ? TProps[TKey] extends AnyListener
        ? TKey
        : never
      : never
  }[keyof TProps],
  string
>

type ModelUpdateListenerPropKey<TProps> = Extract<ListenerPropKey<TProps>, `onUpdate:${string}`>
type EventListenerPropKey<TProps> = Exclude<ListenerPropKey<TProps>, ModelUpdateListenerPropKey<TProps>>

type EventNameFromListenerProp<TKey extends string> = TKey extends `on${infer TName}`
  ? Uncapitalize<TName>
  : never

type ModelKey<TProps> = Extract<
  {
    [TKey in keyof TProps]: TKey extends string
      ? `onUpdate:${TKey}` extends keyof TProps
        ? TKey
        : never
      : never
  }[keyof TProps],
  string
>

type ModalProps<TProps> = Partial<Omit<TProps, ListenerPropKey<TProps>>>

type ModalListeners<TProps> = {
  [TKey in EventListenerPropKey<TProps> as EventNameFromListenerProp<TKey>]?: TProps[TKey]
} & {
  [TKey in EventListenerPropKey<TProps>]?: TProps[TKey]
}

type ModalModels<TProps> = {
  [TKey in ModelKey<TProps>]?: Ref<TProps[TKey]>
}

export type ModalOpenOptions<TComponent extends Component> = {
  props?: ModalProps<ComponentProps<TComponent>>
  listeners?: ModalListeners<ComponentProps<TComponent>>
  models?: ModalModels<ComponentProps<TComponent>>
}

const stack = ref<RuntimeModalEntry[]>([])
let nextModalId = 1

const isOpen = computed(() => {
  return stack.value.length > 0
})

const currentModal = computed<RuntimeModalEntry | null>(() => {
  const current = stack.value.at(-1)

  return current ?? null
})

const component = computed(() => {
  return currentModal.value?.component ?? null
})

const props = computed<RuntimeProps>(() => {
  return currentModal.value?.props ?? {}
})

const listeners = computed<RuntimeListeners>(() => {
  return currentModal.value?.listeners ?? {}
})

const models = computed<RuntimeModels>(() => {
  return currentModal.value?.models ?? {}
})

const toPascalCase = (value: string) => {
  return value.replace(/(^\w|[-_:]\w)/g, (segment) => {
    return segment.replace(/[-_:]/, '').toUpperCase()
  })
}

const toListenerProp = (eventName: string) => {
  if (eventName.startsWith('on')) {
    return eventName
  }

  return `on${toPascalCase(eventName)}`
}

const normalizeListeners = (eventListeners?: Record<string, unknown>) => {
  if (!eventListeners) {
    return {}
  }

  return Object.entries(eventListeners).reduce((acc, [eventName, handler]) => {
    if (typeof handler !== 'function') {
      return acc
    }

    acc[toListenerProp(eventName)] = handler as AnyListener
    return acc
  }, {} as RuntimeListeners)
}

const getIndex = (id?: number) => {
  if (stack.value.length === 0) {
    return -1
  }

  if (id === undefined) {
    return stack.value.length - 1
  }

  return stack.value.findIndex((entry) => entry.id === id)
}

const close = (value?: unknown, id?: number) => {
  const index = getIndex(id)

  if (index < 0) {
    return
  }

  const [entry] = stack.value.splice(index, 1)
  const openModel = entry?.models.open
  if (isRef(openModel)) {
    openModel.value = false
  }
  entry?.resolve(value)
}

const reject = (id?: number) => {
  const index = getIndex(id)

  if (index < 0) {
    return
  }

  const [entry] = stack.value.splice(index, 1)
  const openModel = entry?.models.open
  if (isRef(openModel)) {
    openModel.value = false
  }
  entry?.reject()
}

const closeById = (id: number, value?: unknown) => {
  close(value, id)
}

const rejectById = (id: number) => {
  reject(id)
}

const open = <TComponent extends Component, TResult = unknown>(
  cmp: TComponent,
  options: ModalOpenOptions<TComponent> = {},
) => {
  const id = nextModalId
  nextModalId += 1

  return new Promise<TResult>((resolve, rejectOpen) => {
    const normalizedProps = { ...(options.props ?? {}) } as RuntimeProps
    const normalizedListeners = normalizeListeners(
      options.listeners as Record<string, unknown> | undefined,
    )
    const normalizedModels = { ...((options.models ?? {}) as RuntimeModels) }

    stack.value.push(markRaw({
      id,
      component: markRaw(cmp),
      props: normalizedProps,
      listeners: markRaw(normalizedListeners),
      models: markRaw(normalizedModels),
      resolve: resolve as (value: unknown) => void,
      reject: rejectOpen,
    }))
  })
}

export const useModal = () => {
  return {
    stack,
    isOpen,
    component,
    props,
    listeners,
    models,
    close,
    closeById,
    reject,
    rejectById,
    open,
  }
}
